import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiUsers, FiTarget, FiEye, FiAward } from 'react-icons/fi';
import SEO from '../components/SEO';

const zoomIn = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1.1);
  }
`;

const AboutContainer = styled.div`
  padding-top: 0;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, rgba(26, 77, 128, 0.8) 0%, rgba(44, 62, 80, 0.7) 100%), url('/IMG-20250802-WA0055.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  color: ${props => props.theme.colors.white};
  padding: 120px 0 80px;
  text-align: center;
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  animation: ${zoomIn} 10s ease-in-out infinite;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    background-attachment: scroll;
    min-height: 50vh;
    padding: 100px 0 60px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Section = styled.section`
  padding: 80px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.secondary};
    margin: 20px auto 0;
    border-radius: 2px;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const AboutContent = styled.div`
  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 20px;
    font-size: 1.8rem;
  }
  
  p {
    color: #000000;
    line-height: 1.8;
    margin-bottom: 20px;
    font-size: 1.1rem;
    font-family: 'Times New Roman', Times, serif;
  }
  
  ul {
    list-style: none;
    padding: 0;
  }
  
  li {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
    color: #000000;
    font-family: 'Times New Roman', Times, serif;
    
    svg {
      color: ${props => props.theme.colors.secondary};
      flex-shrink: 0;
    }
  }
`;



const TimelineSection = styled.section`
  background: ${props => props.theme.colors.light};
  padding: 80px 0;
`;

const Timeline = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.theme.colors.secondary};
    transform: translateX(-50%);
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      left: 20px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  margin-bottom: 50px;
  display: flex;
  align-items: center;
  
  &:nth-child(even) {
    flex-direction: row-reverse;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      flex-direction: row;
    }
  }
  
  .timeline-content {
    background: ${props => props.theme.colors.white};
    padding: 30px;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    width: 45%;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      width: calc(100% - 60px);
      margin-left: 60px;
    }
    
    h3 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 10px;
    }
    
    p {
      color: #000000;
      line-height: 1.6;
      font-family: 'Times New Roman', Times, serif;
    }
  }
  
  .timeline-dot {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    background: ${props => props.theme.colors.secondary};
    border-radius: 50%;
    border: 4px solid ${props => props.theme.colors.white};
    box-shadow: 0 0 0 4px ${props => props.theme.colors.secondary};
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      left: 20px;
    }
  }
`;

const WhoWeAreSection = styled.section`
  background: #f9f8f8;
  padding: 80px 0;
  border-bottom: 1px solid #ccc9c9;
  border-bottom-style: dashed;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const WhoWeAreContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  
  p {
    color: #000000;
    line-height: 1.8;
    margin-bottom: 20px;
    font-size: 1.1rem;
    text-align: justify;
    font-family: 'Times New Roman', Times, serif;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: 1rem;
      text-align: left;
    }
  }
`;

const About = () => {


  const timeline = [
    {
      year: "1988",
      title: "Company Founded",
      description: "Trimurthy Tent House was established in Bangalore with a vision to provide quality tent solutions."
    },
    {
      year: "1995",
      title: "Expansion",
      description: "Expanded services to include exhibition stalls and corporate event management."
    },
    {
      year: "2005",
      title: "Modernization",
      description: "Introduced German tents and advanced equipment for premium events."
    },
    {
      year: "2015",
      title: "Digital Transformation",
      description: "Embraced technology and launched online booking and consultation services."
    },
    {
      year: "2024",
      title: "Industry Leader",
      description: "Became one of Bangalore's most trusted tent house suppliers with 36 years of excellence."
    }
  ];

  return (
    <AboutContainer>
      <SEO
        title="Premium Tent House Supplier In Bangalore | Trimurthy Tent House"
        description="Trimurthy Tent House is a trusted tent house supplier in Bangalore offering German hangers, premium tents and event solutions. Call 88808 28698."
        canonical="https://ttheventorganisers.in/about"
      />
      <HeroSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)', color: '#ffffff' }}>About Trimurthy Tent House</h1>
            <p style={{ 
              fontSize: '1.5rem', 
              opacity: 0.95, 
              maxWidth: '600px', 
              margin: '20px auto 0',
              textShadow: '1px 1px 6px rgba(0,0,0,0.5)',
              color: '#ffffff',
              fontWeight: 600,
              fontFamily: "'Times New Roman', Times, serif"
            }}>
              Three Decades of Excellence in Creating Magical Event Experiences Across Bangalore
            </p>
          </motion.div>
        </Container>
      </HeroSection>

      <WhoWeAreSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <SectionTitle style={{ textTransform: 'uppercase' }}>
              Who We Are
            </SectionTitle>
            <WhoWeAreContent>
              <p>
                Our firm, known as Trimurthy Tent House, was established in the year 1988 by three visionary partners to provide shamiyana tents and furniture rental services for weddings, conventions, and corporate functions in Bangalore. In our early stages, we focused on basic tent solutions, but over the years, we have expanded to provide a comprehensive range of premium tent products and event management services, with German Hangers being our flagship offering.
              </p>
              
              <p>
                Today, we are a highly renowned tent house supplier and event management company specializing in German Hangers and premium tent solutions for exhibitions, weddings, corporate conventions, conferences, product launches, and trade shows. Our extensive product range includes German Hangers, German tents, pagoda tents, air-conditioned tents, super structures, mega hangers, exhibition octanorm stalls, platforms, and waterproof sheds for large-scale events and exhibitions. Our German Hangers are particularly popular for corporate events, trade shows, and large gatherings requiring spacious, professional tent structures.
              </p>
              
              <p>
                Trimurthy Tent House has successfully attained market credibility and won the hearts of clients across Karnataka and beyond. Our judicious effort, backed by a team of qualified and experienced professionals, enables us to offer unmatched quality services at industry-leading prices to ensure maximum customer satisfaction.
              </p>
            </WhoWeAreContent>
          </motion.div>
        </Container>
      </WhoWeAreSection>

      <Section>
        <Container>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Mission & Vision
          </SectionTitle>
          <AboutGrid>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <AboutContent>
                <h3><FiTarget style={{ marginRight: '10px' }} />Our Mission</h3>
                <p>
                  We're here to provide the best tent solutions and event management services that our customers can rely on. Our goal is simple - to make every event memorable and successful.
                </p>
                <p>
                  We believe in building lasting relationships with our clients through honest service, quality products, and reliable support. When you choose us, you're choosing a partner who truly cares about your event's success.
                </p>
              </AboutContent>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <AboutContent>
                <h3><FiEye style={{ marginRight: '10px' }} />Our Vision</h3>
                <p>
                  We want to be the go-to tent house in Bangalore - the name that comes to mind when people think of quality event solutions. We're not just about tents; we're about making events happen smoothly.
                </p>
                <p>
                  Our dream is to grow beyond Bangalore while keeping that personal touch that our customers love. We want to set the standard for what great service looks like in this industry.
                </p>
              </AboutContent>
            </motion.div>
          </AboutGrid>
        </Container>
      </Section>

      <TimelineSection>
        <Container>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Journey
          </SectionTitle>
          <Timeline>
            {timeline.map((item, index) => (
              <TimelineItem
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="timeline-content">
                  <h3>{item.year} - {item.title}</h3>
                  <p>{item.description}</p>
                </div>
                <div className="timeline-dot" />
              </TimelineItem>
            ))}
          </Timeline>
        </Container>
      </TimelineSection>
    </AboutContainer>
  );
};

export default About; 